import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Package, ClipboardCheck, AlertTriangle, Clock, Plus, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  StatusBadge,
  PageHeader,
  MetricCard,
  KPIGrid,
  DataTable,
  Column,
  TableFilters,
} from '@/components/common';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  goodsReceiptApi,
  GoodsReceipt as GoodsReceiptType,
  GoodsReceiptDashboard,
  PendingPO,
} from '@/api/services/goods-receipt.api';
import { locationsApi, Location } from '@/api/services/locations.api';

export default function GoodsReceipt() {
  const navigate = useNavigate();
  const [receipts, setReceipts] = useState<GoodsReceiptType[]>([]);
  const [pendingPOs, setPendingPOs] = useState<PendingPO[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [dashboard, setDashboard] = useState<GoodsReceiptDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Form state
  const [selectedPO, setSelectedPO] = useState<string>('');
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [carrier, setCarrier] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [receiptsRes, dashboardRes, pendingRes, locationsRes] = await Promise.all([
        goodsReceiptApi.getAll({ limit: 50 }),
        goodsReceiptApi.getDashboard(),
        goodsReceiptApi.getPendingPOs(),
        locationsApi.getAll({ limit: 100 }),
      ]);
      setReceipts(receiptsRes.data);
      setDashboard(dashboardRes);
      setPendingPOs(pendingRes);
      setLocations(locationsRes.data);
    } catch (error) {
      toast.error('Failed to load goods receipt data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReceipt = async () => {
    if (!selectedPO || !selectedLocation) {
      toast.error('Please select a PO and location');
      return;
    }

    try {
      await goodsReceiptApi.createFromPO({
        purchaseOrderId: selectedPO,
        locationId: selectedLocation,
        deliveryNoteNumber: deliveryNote || undefined,
        carrierName: carrier || undefined,
        notes: notes || undefined,
      });
      toast.success('Goods receipt created');
      setCreateModalOpen(false);
      resetCreateForm();
      loadData();
    } catch (error) {
      toast.error('Failed to create goods receipt');
    }
  };

  const resetCreateForm = () => {
    setSelectedPO('');
    setSelectedLocation('');
    setDeliveryNote('');
    setCarrier('');
    setNotes('');
  };

  const filteredReceipts = receipts.filter((receipt) => {
    const matchesSearch =
      receipt.receiptNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt.purchaseOrder?.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt.shipment?.shipmentNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt.shipment?.trackingNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || receipt.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const receiptsColumns: Column<GoodsReceiptType>[] = [
    {
      key: 'receiptNumber',
      header: 'Receipt #',
      sortable: true,
      linkTo: (receipt) => `/goods-receipt/${receipt.id}`,
    },
    {
      key: 'sourceDocument',
      header: 'Source Document',
      render: (receipt) => (
        <div className="flex flex-col gap-1">
          {receipt.purchaseOrder && (
            <Link
              to={`/purchase-orders/${receipt.purchaseOrder.id}`}
              className="text-sm font-medium text-blue-600 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              PO: {receipt.purchaseOrder.poNumber}
            </Link>
          )}
          {receipt.shipment && (
            <Link
              to={`/shipments/${receipt.shipment.id}`}
              className="text-sm font-medium text-emerald-600 hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              Shipment: {receipt.shipment.shipmentNumber}
            </Link>
          )}
        </div>
      ),
    },
    {
      key: 'location',
      header: 'Location',
      render: (receipt) => <>{receipt.location.name}</>,
    },
    {
      key: 'receivedBy',
      header: 'Received By',
      render: (receipt) => <>{receipt.receivedBy.fullName}</>,
    },
    {
      key: 'items',
      header: 'Items',
      render: (receipt) => (
        <>
          {receipt._count?.items || 0}
          {receipt._count?.discrepancies ? (
            <Badge variant="outline" className="ml-2 text-red-600">
              {receipt._count.discrepancies} issues
            </Badge>
          ) : null}
        </>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (receipt) => <StatusBadge status={receipt.status} />,
    },
    {
      key: 'receivedAt',
      header: 'Received At',
      sortable: true,
      render: (receipt) => <>{format(new Date(receipt.receivedAt), 'MMM d, yyyy HH:mm')}</>,
    },
  ];

  const pendingPOColumns: Column<PendingPO>[] = [
    {
      key: 'poNumber',
      header: 'PO #',
      sortable: true,
      render: (po) => <span className="font-medium">{po.poNumber}</span>,
    },
    {
      key: 'supplier',
      header: 'Supplier',
      render: (po) => <>{po.supplier.name}</>,
    },
    {
      key: 'items',
      header: 'Items',
      render: (po) => <>{po.items.length} items</>,
    },
    {
      key: 'expectedDate',
      header: 'Expected Date',
      sortable: true,
      render: (po) => (
        <>{po.expectedDate ? format(new Date(po.expectedDate), 'MMM d, yyyy') : '-'}</>
      ),
    },
    {
      key: 'totalAmount',
      header: 'Amount',
      sortable: true,
      render: (po) => <>{po.totalAmount ? `$${po.totalAmount.toLocaleString()}` : '-'}</>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (po) => <StatusBadge status={po.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-[100px]',
      render: (po) => (
        <Button
          size="sm"
          onClick={() => {
            setSelectedPO(po.id);
            setCreateModalOpen(true);
          }}
        >
          <Truck className="mr-1 h-3 w-3" />
          Receive
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      {/* Header */}
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Goods Receipt"
          description="Deliveries booked in against a purchase order, with inspection outcome and receiving location."
          actions={
            <Button onClick={() => navigate('/goods-receipt/new')}>
              <Plus className="mr-2 h-4 w-4" />
              New Receipt
            </Button>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <motion.div variants={fadeUp}>
        {dashboard && (
          <KPIGrid columns={4}>
            <MetricCard
              label="Pending"
              value={dashboard.pending}
              icon={<Clock className="h-5 w-5" />}
              sentiment="warning"
            />
            <MetricCard
              label="Partial"
              value={dashboard.partial}
              sentiment="info"
              icon={<Package className="h-5 w-5" />}
            />
            <MetricCard
              label="Completed Today"
              value={dashboard.completedToday}
              icon={<ClipboardCheck className="h-5 w-5" />}
              sentiment="success"
            />
            <MetricCard
              label="Open Discrepancies"
              value={dashboard.openDiscrepancies}
              icon={<AlertTriangle className="h-5 w-5" />}
              sentiment="danger"
            />
          </KPIGrid>
        )}

        {/* Tabs */}
        <Tabs defaultValue="receipts" className="space-y-6">
          <TabsList>
            <TabsTrigger value="receipts">Goods Receipts</TabsTrigger>
            <TabsTrigger value="pending">Pending POs ({pendingPOs.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="receipts" className="space-y-6">
            {/* Receipts Table */}
            <DataTable<GoodsReceiptType>
              toolbar={
                <TableFilters
                  search={searchTerm}
                  onSearchChange={setSearchTerm}
                  searchPlaceholder="Search by receipt or PO number..."
                  filters={[
                    {
                      id: 'status',
                      label: 'Statuses',
                      value: statusFilter,
                      options: [
                        { value: 'PENDING', label: 'Pending' },
                        { value: 'PARTIALLY_RECEIVED', label: 'Partial' },
                        { value: 'COMPLETED', label: 'Completed' },
                        { value: 'CANCELLED', label: 'Cancelled' },
                      ],
                      onChange: setStatusFilter,
                      placeholder: 'Status',
                      width: 'w-[180px]',
                    },
                  ]}
                />
              }
              onRowClick={(receipt) => navigate(`/goods-receipt/${receipt.id}`)}
              data={filteredReceipts}
              columns={receiptsColumns}
              pagination
              emptyTitle="No goods receipts found"
            />
          </TabsContent>

          <TabsContent value="pending" className="space-y-6">
            {/* Pending POs Table */}
            <div className="space-y-4">
              <div className="px-1">
                <h3 className="text-base font-medium text-foreground">
                  Purchase Orders Awaiting Receipt
                </h3>
                <p className="text-sm text-muted-foreground">
                  Select a PO to create a new goods receipt
                </p>
              </div>
              <DataTable<PendingPO>
                toolbar={
                  <TableFilters
                    search={searchTerm}
                    onSearchChange={setSearchTerm}
                    searchPlaceholder="Search by receipt or PO number..."
                    filters={[
                      {
                        id: 'status',
                        label: 'Statuses',
                        value: statusFilter,
                        options: [
                          { value: 'PENDING', label: 'Pending' },
                          { value: 'PARTIALLY_RECEIVED', label: 'Partial' },
                          { value: 'COMPLETED', label: 'Completed' },
                          { value: 'CANCELLED', label: 'Cancelled' },
                        ],
                        onChange: setStatusFilter,
                        placeholder: 'Status',
                        width: 'w-[180px]',
                      },
                    ]}
                  />
                }
                data={pendingPOs}
                columns={pendingPOColumns}
                pagination
                emptyTitle="No pending purchase orders"
              />
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Receipt Modal */}
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Goods Receipt</DialogTitle>
              <DialogDescription>
                Create a new goods receipt from a purchase order
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Purchase Order *</Label>
                <Select value={selectedPO} onValueChange={setSelectedPO}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select PO" />
                  </SelectTrigger>
                  <SelectContent>
                    {pendingPOs.map((po) => (
                      <SelectItem key={po.id} value={po.id}>
                        {po.poNumber} - {po.supplier.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Receiving Location *</Label>
                <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name} ({loc.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Delivery Note Number</Label>
                <Input
                  value={deliveryNote}
                  onChange={(e) => setDeliveryNote(e.target.value)}
                  placeholder="Enter delivery note number"
                />
              </div>
              <div className="space-y-2">
                <Label>Carrier Name</Label>
                <Input
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  placeholder="Enter carrier name"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateReceipt}>Create Receipt</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </motion.div>
    </motion.main>
  );
}
